import React, { useState, useEffect, useMemo } from 'react';
import {
  Typography, Divider, FormControl, InputLabel, Select, MenuItem,
  Box, Button, Grid, CardMedia, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Link, Tabs, Tab, styled,
  Container, Card
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import moment from 'moment';
import 'moment/locale/fr';
import { getPublications } from '../services/publicationService';
import NavigationBar from '../navbar/NavigationBar';
import Footer from '../navbar/Footer';
import { createTheme, ThemeProvider } from '@mui/material/styles';

// Thème (identique à l'original)
const theme = createTheme({
  palette: {
    primary: { main: '#388E3C', contrastText: '#ffffff' },
    secondary: { main: '#2E7D32' },
    background: { default: '#f8f9fa', paper: '#ffffff' },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: { fontWeight: 600, fontSize: '1.8rem', color: '#2E7D32' },
    h6: { fontWeight: 500, fontSize: '1.1rem', lineHeight: 1.2 },
    body1: { fontSize: '0.95rem', color: '#4a4a4a' },
    body2: { fontSize: '0.85rem', color: '#6c757d' },
  },
  shape: { borderRadius: 8 },
});

// Composants stylisés pour VisaActor
const StyledSelect = styled(Select)(({ theme }) => ({
  minWidth: 250,
  backgroundColor: theme.palette.background.paper,
  '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.light, borderWidth: 2 },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.dark, borderWidth: 2 },
}));

const DocumentCard = styled(Card)(({ theme }) => ({
  transition: 'transform 0.3s, box-shadow 0.3s',
  '&:hover': { transform: 'translateY(-4px)', boxShadow: theme.shadows[6] },
}));

function Visa() {
  const [tabValue, setTabValue] = useState(0); // 0 = Documents requis, 1 = Visas octroyés
  // États pour VisaActor
  const [visibleCount, setVisibleCount] = useState(4);
  const [selectedYear, setSelectedYear] = useState('Tous');
  const [visaData, setVisaData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Chargement des visas octroyés
  useEffect(() => {
    setLoading(true);
    getPublications({ type: 'vs', perPage: 1000 })
      .then(res => {
        if (res.error) throw new Error(res.error);
        const mapped = res.data.map(item => ({
          title: item.title,
          link: item.document_url,
          image: item.image_url,
          description: item.description,
          date: new Date(item.date)
        }));
        setVisaData(mapped.sort((a, b) => b.date - a.date));
      })
      .catch(err => { setError('Erreur lors du chargement des données.'); console.error(err); })
      .finally(() => setLoading(false));
  }, []);

  const filteredDocs = useMemo(() => {
    if (selectedYear === 'Tous') return visaData;
    return visaData.filter(doc => doc.date.getFullYear().toString() === selectedYear);
  }, [visaData, selectedYear]);

  const yearOptions = useMemo(() => {
    const years = [...new Set(visaData.map(doc => doc.date.getFullYear()))];
    return ['Tous', ...years.sort((a, b) => b - a)];
  }, [visaData]);

  const handleYearChange = (event) => {
    setSelectedYear(event.target.value);
    setVisibleCount(4);
  };

  // --- Onglet Documents requis (contenu EXACT de VisaInformation) ---
  const DocumentsTab = () => (
    <Box>
      <Typography variant="h4" align="justify" gutterBottom style={{ color: '#388E3C' }}>
        INFORMATIONS SUR LE VISA STATISTIQUE
      </Typography>
      <Typography variant="body1" style={{ marginBottom: '16px' }}>
        Pour obtenir un visa statistique auprès de l’Institut National de la Statistique du Burundi, il est impératif de fournir les documents suivants :
      </Typography>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell style={{ fontWeight: 'bold', color: '#388E3C' }}><Typography variant="body1">Document</Typography></TableCell>
              <TableCell style={{ fontWeight: 'bold', color: '#388E3C' }}><Typography variant="body1">Description</Typography></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell><Typography variant="body1">Fiche d'information sur le visa</Typography></TableCell>
              <TableCell><Typography variant="body1">Obligatoire</Typography></TableCell>
            </TableRow>
            <TableRow>
              <TableCell><Typography variant="body1">Lettre de Demande de Visa Statistique</Typography></TableCell>
              <TableCell>
                <Typography variant="body1">
                  Cette lettre doit être adressée au Directeur Général de l’Institut National de la Statistique (INSBU) et Président du Comité Technique de l’Information Statistique (CTIS). Veuillez vous conformer aux dispositions de l’article  
                  <Link href="/documents/ordinances/ordonance540cabsen1643.pdf" target="_blank" rel="noopener noreferrer" color="inherit">
                    22 de la Loi n°1/18 du 24 juillet 2023
                  </Link>.
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell><Typography variant="body1">Avis d’Éthique</Typography></TableCell>
              <TableCell><Typography variant="body1">Ce document est requis en cas d’enquête ou d’étude biomédicale ou comportementale.</Typography></TableCell>
            </TableRow>
            <TableRow>
              <TableCell><Typography variant="body1">Fiche de Renseignement</Typography></TableCell>
              <TableCell><Typography variant="body1">Cette fiche peut être obtenue auprès du Secrétariat du CTIS.</Typography></TableCell>
            </TableRow>
            <TableRow>
              <TableCell><Typography variant="body1">Documents de l’Enquête</Typography></TableCell>
              <TableCell><Typography variant="body1">Soumettez deux exemplaires de ces documents ainsi qu’une version électronique.</Typography></TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="h6" style={{ marginTop: '16px', color: '#388E3C' }}>
        Ces documents de l’enquête doivent inclure :
      </Typography>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell style={{ fontWeight: 'bold', color: '#388E3C' }}><Typography variant="body1">Document</Typography></TableCell>
              <TableCell style={{ fontWeight: 'bold', color: '#388E3C' }}><Typography variant="body1">Description</Typography></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell><Typography variant="body1">Méthodologie de l’Enquête</Typography></TableCell>
              <TableCell><Typography variant="body1">Énoncez la méthode de collecte, le calendrier de publication, ainsi que les modalités de diffusion et d’archivage des résultats.</Typography></TableCell>
            </TableRow>
            <TableRow>
              <TableCell><Typography variant="body1">Questionnaires ou Guide d’Entretien</Typography></TableCell>
              <TableCell><Typography variant="body1">Veillez à ce qu’ils soient traduits en kirundi.</Typography></TableCell>
            </TableRow>
            <TableRow>
              <TableCell><Typography variant="body1">Consentement Éclairé</Typography></TableCell>
              <TableCell><Typography variant="body1">Fournissez-le traduit en kirundi pour toute enquête ou étude biomédicale.</Typography></TableCell>
            </TableRow>
            <TableRow>
              <TableCell><Typography variant="body1">Manuels</Typography></TableCell>
              <TableCell><Typography variant="body1">Incluez les manuels de l’agent enquêteur, du contrôleur, du superviseur, etc.</Typography></TableCell>
            </TableRow>
            <TableRow>
              <TableCell><Typography variant="body1">Curricula Vitae (CV)</Typography></TableCell>
              <TableCell><Typography variant="body1">Joignez les CV des principaux investigateurs.</Typography></TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="body1" style={{ marginTop: '16px' }}>
        <strong>Remarque Importante : </strong>
        Il est primordial d’inclure tous ces documents dans le dossier de demande de visa statistique et de les envoyer à l’adresse e-mail suivants:
        <Link href="mailto:ins.burundi@insbu.bi" color="inherit"> ins.burundi@insbu.bi</Link> / <Link href="mailto:ins.burundi2022@gmail.com" color="inherit"> ins.burundi2022@gmail.com</Link>
      </Typography>
      <Typography variant="body1" style={{ marginTop: '8px' }}>
        <strong>Note : </strong>
        Les documents mentionnés sont obligatoires et doivent être présentés dans leur intégralité. Veuillez respecter ces exigences afin d’assurer le bon déroulement de votre demande.
      </Typography>
      <Typography variant="body1">
        <strong>Pour plus d’informations, veuillez télécharger le document PDF : </strong>
        <a href="/documents/visa/Fiche_visa_Insbu.pdf" target="_blank" rel="noopener noreferrer" style={{ color: 'green', textDecoration: 'underline' }}>
          Télécharger le PDF
        </a>
      </Typography>
    </Box>
  );

  // --- Onglet Visas octroyés (contenu EXACT de VisaActor) ---
  const VisasTab = () => {
    if (loading) {
      return (
        <>
          <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>Visa Statistiques Octroyés</Typography>
          <Divider sx={{ mb: 3 }} />
          <Typography variant="body1">Chargement des données...</Typography>
        </>
      );
    }
    if (error) {
      return (
        <>
          <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>Visa Statistiques Octroyés</Typography>
          <Divider sx={{ mb: 3 }} />
          <Typography variant="body1" color="error">{error}</Typography>
        </>
      );
    }
    return (
      <>
        <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>Visa Statistiques Octroyés</Typography>
        <Divider sx={{ mb: 3 }} />
        <Typography variant="body1" paragraph sx={{ mb: 4 }}>
          Consultez les visas statistiques officiels accordés par l'Autorité Statistique Nationale.
          Ces documents valident les méthodologies et protocoles des enquêtes menées au Burundi.
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
          <FormControl variant="outlined" sx={{ minWidth: 250 }}>
            <InputLabel id="year-filter-label">Année de publication</InputLabel>
            <StyledSelect
              labelId="year-filter-label"
              value={selectedYear}
              onChange={handleYearChange}
              label="Année de publication"
            >
              {yearOptions.map(year => (
                <MenuItem key={year} value={year.toString()}>
                  {year === 'Tous' ? 'Toutes les années' : year}
                </MenuItem>
              ))}
            </StyledSelect>
          </FormControl>
        </Box>

        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          {filteredDocs.length} résultat(s) trouvé(s)
        </Typography>

        <Grid container spacing={1}>
          {filteredDocs.slice(0, visibleCount).map((doc, index) => (
            <Grid item xs={12} key={index}>
              <DocumentCard elevation={2}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, height: '100%' }}>
                  <a href={doc.link} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>
                    <CardMedia
                      component="img"
                      sx={{ width: { xs: '100%', sm: 180 }, height: { xs: 200, sm: 'auto' }, objectFit: 'cover' }}
                      image={doc.image}
                      alt={doc.title}
                    />
                  </a>
                  <Box sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h6" component="h2" gutterBottom>
                      <a href={doc.link} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>
                        <DownloadIcon /> {doc.title}
                      </a>
                    </Typography>
                    <Typography variant="body1" paragraph sx={{ flex: 1 }}>
                      {doc.description}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Publié le {moment(doc.date).locale('fr').format('LL')}
                    </Typography>
                  </Box>
                </Box>
              </DocumentCard>
            </Grid>
          ))}
        </Grid>

        {visibleCount < filteredDocs.length && (
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Button
              variant="contained"
              onClick={() => setVisibleCount(prev => prev + 4)}
              sx={{ px: 4, py: 1.5, fontWeight: 500, textTransform: 'none', fontSize: '1rem' }}
            >
              Afficher plus de visas
            </Button>
          </Box>
        )}
      </>
    );
  };

  // Layout principal
  return (
    <ThemeProvider theme={theme}>
      <div style={{ backgroundColor: theme.palette.background.default, minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <NavigationBar />
        <Container maxWidth="lg" sx={{ py: 4, flex: 1 }}>
          <Tabs value={tabValue} onChange={(e, newVal) => setTabValue(newVal)} sx={{ mb: 3 }}>
            <Tab label="Documents requis" />
            <Tab label="Visas octroyés" />
          </Tabs>
          {tabValue === 0 && <DocumentsTab />}
          {tabValue === 1 && <VisasTab />}
        </Container>
        <Footer />
      </div>
    </ThemeProvider>
  );
}

export default Visa;